import { defineStore } from 'pinia'
import { DateManager } from '../services/dateManager'
import { devLog, devWarn, devError, devDebug, isDevEnvironment } from '@/services/logger'

// Tipos básicos para mensagem e sala
export interface Message {
  _id: string | number
  content: string
  senderId: string | number
  date?: string
  timestamp?: string
  failure?: boolean
  seen?: boolean
  distributed?: boolean
  replyMessage?: any // Permite incluir dados da mensagem original em respostas
  channel?: string
  [key: string]: any
}

export interface Room {
  roomId: string | number
  roomName: string
  users: any[]
  unreadCount: number
  index?: string | number // propriedade opcional para vue-advanced-chat
  current_stage: any
  temperature: any
  assigned_to_details: any
  vehicle_details: any
  brand: string
  status: any
  last_message_at: string
  company: any
  source: any
  avatar?: string
  phone?: string
  email?: string
  notes?: string
  contact_uuid?: string
  typingUsers?: string[]
}

export interface ChatState {
  messagesByRoomId: Record<string | number, Message[]>
  rooms: Room[]
  activeRoomId: string | number | null
  lastCommunicationByRoom: Record<string | number, string | number>
  lastIncomingByRoom: Record<string | number, string | number>
}

const summarizeMessage = (message: any) => {
  if (!message || typeof message !== 'object') {
    return { hasMessage: false }
  }

  const contentLength = typeof message.content === 'string' ? message.content.length : undefined
  const filesCount = Array.isArray(message.files) ? message.files.length : undefined

  return {
    id: message.id ?? message._id ?? message.temporary_id ?? null,
    contentLength,
    filesCount,
    reaction: message.reaction ?? undefined,
    communication: message.communication ?? undefined,
  }
}

const summarizeRoom = (room: any) => {
  if (!room || typeof room !== 'object') {
    return { hasRoom: false }
  }

  return {
    id: room.roomId ?? room.id ?? room.room_id ?? null,
    name: room.roomName ?? room.name ?? undefined,
    unreadCount: room.unreadCount ?? room.unreadMessages ?? undefined,
    lastMessageAt: room.last_message_at ?? room.lastMessageAt ?? undefined,
  }
}

const extractErrorMessage = (error: unknown) => (error instanceof Error ? error.message : String(error))

export const useChatStore = defineStore('chat', {
  state: (): ChatState => ({
    messagesByRoomId: {},
    rooms: [],
    activeRoomId: null,
    lastCommunicationByRoom: {},
    lastIncomingByRoom: {},
  }),
  actions: {
    setActiveRoom(roomId: string | number) {
      this.activeRoomId = roomId
    },
    addMessage(roomId: string | number, message: Message) {
      // ✅ VALIDAÇÃO SUPER ROBUSTA
      if (!roomId && roomId !== 0) {
        devWarn('[ChatStore] addMessage: roomId inválido', {
          roomId,
          message: summarizeMessage(message),
        })
        return
      }

      // ✅ CONVERSÃO SEGURA para string
      const safeRoomId = String(roomId)
      if (safeRoomId === 'null' || safeRoomId === 'undefined' || safeRoomId === '') {
        devWarn('[ChatStore] addMessage: roomId inválido após conversão', {
          roomId,
          safeRoomId,
          message: summarizeMessage(message),
        })
        return
      }

      if (!message || typeof message !== 'object') {
        devWarn('[ChatStore] addMessage: message inválido', {
          roomId: safeRoomId,
          message: summarizeMessage(message),
        })
        return
      }

      if (!this.messagesByRoomId[safeRoomId]) {
        this.messagesByRoomId[safeRoomId] = []
      }

      if (message.reaction && message.communication) {
        const messageFound = this.messagesByRoomId[safeRoomId].find((m) => m._id === message.communication)
        if (messageFound) {
          if (!messageFound.reactions) {
            messageFound.reactions = {}
          }

          messageFound.reactions[message.reaction] = [safeRoomId]

          devLog('[ChatStore] addMessage: Reação adicionada', {
            roomId: safeRoomId,
            reaction: message.reaction,
          })
          return
        } else {
          devWarn('[ChatStore] addMessage: Mensagem para reação não encontrada', {
            roomId: safeRoomId,
            communicationId: message.communication,
          })
          return
        }
      }

      // Busca o identificador temporário da mensagem (temporary_id, _id ou id)
      const tempId = message.temporary_id || message._id || message.id
      const updated = [...this.messagesByRoomId[safeRoomId]]

      // Procura se já existe uma mensagem com o mesmo identificador
      const existingIndex = updated.findIndex((m) => m.temporary_id === tempId || m._id === tempId || m.id === tempId)

      if (existingIndex !== -1) {
        // Se já existe, atualiza os dados da mensagem existente com os novos dados recebidos
        const prev = updated[existingIndex]
        const incoming = message as any

        // ✅ CORREÇÃO CRÍTICA: Mesclar arquivos preservando previews locais
        let mergedFiles = prev.files
        if (Array.isArray(incoming.files)) {
          // Criar mapas para merge inteligente
          const prevById = new Map<string | number, any>((prev.files || []).map((f: any) => [f.id || f._id, f]))

          mergedFiles = incoming.files.map((incomingFile: any) => {
            const key = incomingFile.id || incomingFile._id
            const prevFile = prevById.get(key)

            // Se arquivo já existia localmente, preservar preview e URLs locais
            if (prevFile) {
              return {
                ...prevFile, // Dados locais (preview, url local)
                ...incomingFile, // Dados do backend (id definitivo, metadados)
                // ✅ PRESERVAR: URLs locais até download definitivo
                url: prevFile.url || incomingFile.url,
                preview: prevFile.preview || incomingFile.preview,
                // ✅ PRESERVAR: Estado de carregamento se ainda local
                local: prevFile.local !== false ? prevFile.local : incomingFile.local,
                loading: prevFile.loading !== undefined ? prevFile.loading : incomingFile.loading,
              }
            }

            // Arquivo novo do backend
            return incomingFile
          })
        }

        const mergedCreatedAt =
          (incoming as any).createdAt ||
          (incoming as any).external_created_at ||
          (prev as any).createdAt ||
          (prev as any).external_created_at

        updated[existingIndex] = {
          ...prev,
          ...incoming,
          id: (incoming as any).id || (prev as any).id,
          _id: (incoming as any)._id || (prev as any)._id,
          createdAt: mergedCreatedAt,
          files: mergedFiles,
        }
      } else {
        // Se não existe, cria um novo objeto de mensagem para adicionar ao array
        const msg = {
          ...message,
          id: message.id || message._id || message.temporary_id,
          roomId: safeRoomId,
        }

        // Se a mensagem é uma resposta, busca a mensagem original e inclui os dados dela
        if (message.reply_to) {
          const messageFound = this.messagesByRoomId[safeRoomId].find((m) => m._id === message.reply_to)
          if (messageFound) {
            msg.replyMessage = {
              _id: messageFound.id,
              content: messageFound.content,
              senderId: messageFound.senderId,
              // files: messageFound.files || [], // Se quiser incluir arquivos
            }
          }
        }
        updated.push(msg)
      }

      // Ordena por timestamp (ou id como fallback)
      updated.sort((a, b) => {
        const getSafeTimestamp = (item: any) => {
          try {
            if (item.date) {
              const parsed = DateManager.parseBRDate(item.date)
              if (parsed && !isNaN(parsed.getTime())) {
                return parsed.getTime()
              }
              const fallback = new Date(item.date)
              return !isNaN(fallback.getTime()) ? fallback.getTime() : 0
            }
            if (item.createdAt) {
              const parsed = DateManager.parseBRDate(item.createdAt)
              if (parsed && !isNaN(parsed.getTime())) {
                return parsed.getTime()
              }
              const fallback = new Date(item.createdAt)
              return !isNaN(fallback.getTime()) ? fallback.getTime() : 0
            }
            return 0
          } catch (error) {
            devWarn('[Pinia] Erro ao processar timestamp', {
              error: extractErrorMessage(error),
              message: summarizeMessage(item),
            })
            return 0
          }
        }

        const ta = getSafeTimestamp(a)
        const tb = getSafeTimestamp(b)
        if (ta !== tb) return ta - tb

        // ✅ CORREÇÃO: Conversão segura para número
        const idA = Number(a.id) || 0
        const idB = Number(b.id) || 0
        return idA - idB
      })

      // Garante imutabilidade
      this.messagesByRoomId[safeRoomId] = updated
      // Debug log
      if (isDevEnvironment) {
        const messagesForRoom = this.messagesByRoomId[safeRoomId]
        const lastMessage = messagesForRoom[messagesForRoom.length - 1]
        devLog('[Pinia] addMessage (ordenado, sem duplicatas, timestamp normalizado)', {
          roomId: safeRoomId,
          totalMessages: messagesForRoom.length,
          lastMessage: summarizeMessage(lastMessage),
        })
      }
    },
    setMessages(roomId: string | number, initialMessages: Message[]) {
      // Normaliza, remove duplicatas e ordena cronologicamente, como em addMessage
      const byId = new Map<string | number, Message>()

      const normalize = (m: any): Message => {
        const messageId = m.id || m._id || m.temporary_id
        const createdAt = m.createdAt || m.external_created_at || m.timestamp || m.date || null
        return {
          ...m,
          id: messageId,
          _id: m._id || messageId,
          createdAt: createdAt,
        }
      }

      for (const raw of initialMessages || []) {
        const msg = normalize(raw)
        if (!msg.id && !msg._id) continue
        // Se já existir, substitui pela última ocorrência (mais atual)
        byId.set(msg._id || msg.id, msg)
      }

      const deduped = Array.from(byId.values())

      const getSafeTimestamp = (item: any) => {
        try {
          if (item.date) {
            return DateManager.parseBRDate(item.date)?.getTime() || new Date(item.date).getTime() || 0
          }
          if (item.createdAt) {
            return DateManager.parseBRDate(item.createdAt)?.getTime() || new Date(item.createdAt).getTime() || 0
          }
          return 0
        } catch {
          return 0
        }
      }

      deduped.sort((a, b) => {
        const ta = getSafeTimestamp(a)
        const tb = getSafeTimestamp(b)
        if (ta !== tb) return ta - tb
        return (a.id || 0) - (b.id || 0)
      })

      this.messagesByRoomId[roomId] = deduped
    },
    clearMessages(roomId: string | number) {
      this.messagesByRoomId[roomId] = []
    },
    resetChatState() {
      this.messagesByRoomId = {}
      this.rooms = []
      this.activeRoomId = null
      this.lastCommunicationByRoom = {}
      this.lastIncomingByRoom = {}
    },
    updateExternalStatus(dados: any) {
      const { roomId, messageId, temporaryId, externalStatus } = dados
      const payloadSummary = { roomId, messageId, temporaryId, externalStatus }
      if (!roomId || !messageId || !externalStatus) {
        devError('[Pinia] updateExternalStatus: Dados incompletos', payloadSummary)
        return
      }
      // Atualiza a mensagem específica
      const messages = this.messagesByRoomId[roomId]

      if (messages) {
        let message = messages.find((msg) => msg._id === messageId)

        if (!message) {
          message = messages.find((msg) => msg.temporary_id === temporaryId)
        }

        if (message) {
          // Persist external_status for UI components that rely on it
          ;(message as any).external_status = externalStatus
          if (externalStatus == 'sent') {
            // o whatsapp recebeu, não distribuiu e não foi lido
            message.seen = false
            message.distributed = true
            message.failure = false
          } else if (externalStatus == 'delivered') {
            // o whatsapp recebeu e distribuiu
            message.distributed = false
            message.seen = true
            message.failure = false
          } else if (externalStatus == 'read') {
            // o whatsapp recebeu, distribuiu e foi lido
            message.seen = true
            message.distributed = true
            message.failure = false
          } else if (externalStatus == 'failed') {
            // o whatsapp recebeu, mas não enviou, quebrou algo lá
            message.seen = false
            message.distributed = false
            message.failure = true
          }

          devLog('[Pinia] updateExternalStatus: Mensagem atualizada', payloadSummary)
        } else {
          devWarn('[Pinia] updateExternalStatus: Mensagem não encontrada', { roomId, messageId, temporaryId })
        }
      } else {
        devWarn('[Pinia] updateExternalStatus: Sala não encontrada', { roomId })
      }
    },
    updateReaction(dados: any) {
      // ✅ VALIDAÇÃO ROBUSTA
      if (!dados || typeof dados !== 'object') {
        devError('[ChatStore] updateReaction: Dados inválidos', { hasPayload: Boolean(dados), type: typeof dados })
        return
      }

      const { roomId, messageId, temporaryId, reaction } = dados
      const payloadSummary = { roomId, messageId, temporaryId, reaction }

      // ✅ VALIDAÇÃO E CONVERSÃO SEGURA
      if (!roomId && roomId !== 0) {
        devError('[ChatStore] updateReaction: roomId inválido', payloadSummary)
        return
      }

      const safeRoomId = String(roomId)
      if (safeRoomId === 'null' || safeRoomId === 'undefined' || safeRoomId === '') {
        devError('[ChatStore] updateReaction: roomId inválido após conversão', payloadSummary)
        return
      }

      if (!messageId) {
        devError('[ChatStore] updateReaction: messageId inválido', payloadSummary)
        return
      }

      if (!reaction) {
        devError('[ChatStore] updateReaction: reaction inválido', payloadSummary)
        return
      }

      const messages = this.messagesByRoomId[safeRoomId]
      if (!messages || !Array.isArray(messages)) {
        devWarn('[ChatStore] updateReaction: Sala não encontrada', { roomId: safeRoomId })
        return
      }

      let message = messages.find((msg) => msg._id === messageId)

      if (!message && temporaryId) {
        message = messages.find((msg) => msg.temporary_id === temporaryId)
      }

      if (message) {
        if (!message.reactions) {
          message.reactions = {}
        }

        message.reactions[reaction] = [safeRoomId]

        devLog('[Pinia] updateReaction: Mensagem reagida', payloadSummary)
      } else {
        devWarn('[Pinia] updateReaction: Mensagem não encontrada', payloadSummary)
      }
    },
    removeMessage(roomId: string | number, messageId: string | number) {
      if (!this.messagesByRoomId[roomId]) return
      const messageIndex = this.messagesByRoomId[roomId].findIndex((msg) => msg._id === messageId)
      if (messageIndex !== -1) {
        this.messagesByRoomId[roomId].splice(messageIndex, 1)
        devLog('[Pinia] removeMessage: Mensagem removida', { roomId, messageId })
      } else {
        devWarn('[Pinia] removeMessage: Mensagem não encontrada', { roomId, messageId })
      }
    },
    normalizeRoomPayload(rawRoom: any): Room | null {
      if (!rawRoom || typeof rawRoom !== 'object') {
        devWarn('[ChatStore] normalizeRoomPayload: Room inválido', summarizeRoom(rawRoom))
        return null
      }

      const rawId = (rawRoom as any).id ?? (rawRoom as any).roomId ?? (rawRoom as any).room_id
      if (rawId === undefined || rawId === null) {
        devWarn('[ChatStore] normalizeRoomPayload: Room sem ID', summarizeRoom(rawRoom))
        return null
      }

      const tamString = 20
      let brand = ''

      const vehicleDetails =
        rawRoom.vehicle_details && typeof rawRoom.vehicle_details === 'object' ? rawRoom.vehicle_details : {}

      // Legacy vehicle_details (antigo)
      if (vehicleDetails && (vehicleDetails.brand || vehicleDetails.model)) {
        const marca = vehicleDetails.brand || ''
        const modelo = vehicleDetails.model || ''
        brand = [marca, modelo].filter(Boolean).join(' ')
      }

      // Novo: veiculos_proposta (somente leitura via LeadSerializer)
      if (!brand && Array.isArray((rawRoom as any).veiculos_proposta) && (rawRoom as any).veiculos_proposta.length) {
        const first: any = (rawRoom as any).veiculos_proposta[0] || {}
        const v = first.veiculo || first
        const marca = v?.marca?.nome || v?.marca || ''
        const modelo = v?.modelo?.nome || v?.modelo || ''
        const versao = v?.versao?.nome || v?.versao || ''
        brand = [marca, modelo, versao].filter(Boolean).join(' ')
      }

      brand = brand.length > tamString ? brand.slice(0, tamString) + '...' : brand

      const currentUserId = typeof localStorage !== 'undefined' ? localStorage.getItem('cmpU') : null
      const currentUserName = typeof localStorage !== 'undefined' ? localStorage.getItem('cmpUName') : null

      let users = Array.isArray(rawRoom.users) ? [...rawRoom.users] : []
      if (users.length === 0 && currentUserId) {
        users = [
          {
            _id: currentUserId,
            username: currentUserName,
            avatar: undefined,
          },
          {
            _id: rawId,
            username: rawRoom.name,
            avatar: undefined,
          },
        ]
      }

      const lastCommunication = rawRoom.last_communication || null
      const lastMessageAt =
        rawRoom.last_message_at || (lastCommunication && lastCommunication.created_at) || new Date().toISOString()

      const unreadFromPayload =
        typeof rawRoom.unreadMessages === 'number'
          ? rawRoom.unreadMessages
          : typeof rawRoom.unreadCount === 'number'
            ? rawRoom.unreadCount
            : 0

      const typingUsers = Array.isArray(rawRoom.typingUsers) ? rawRoom.typingUsers.map((id: any) => String(id)) : []

      const buildAvatar = () => {
        if (lastCommunication?.channel === 'email') return 'gmail.png'
        if (lastCommunication?.channel === 'whatsapp message') return 'whatsapp.png'
        if (lastCommunication?.channel === 'instagram') return 'instagram.png'
        return undefined
      }

      return {
        roomId: rawId,
        roomName: rawRoom.name || rawRoom.roomName || 'Unnamed Room',
        users,
        unreadCount: unreadFromPayload,
        index: rawId,
        current_stage: rawRoom.current_stage || {},
        temperature: rawRoom.temperature || 'cold',
        assigned_to_details: rawRoom.assigned_to_details || {},
        vehicle_details: vehicleDetails,
        brand,
        status: rawRoom.status || '',
        last_message_at: lastMessageAt,
        company: rawRoom.company || {},
        source: rawRoom.source || 'unknown',
        avatar: buildAvatar(),
        phone: rawRoom.phone || rawRoom.lead_phone || rawRoom.customer_phone || '',
        email: rawRoom.email || rawRoom.lead_email || rawRoom.customer_email || '',
        notes: rawRoom.notes || '',
        contact_uuid: rawRoom.contact_uuid || null,
        typingUsers,
      }
    },
    addRoom(room: any, filtered: boolean | null = null) {
      // Durante buscas filtradas, a lista já é limpa antes do loop.
      // Evita apagar resultados já adicionados ao reinserir cada lead.
      if (filtered != null && this.rooms.length === 0) {
        this.rooms = []
      }

      const created = this.upsertRoom(room)
      if (!created) {
        devWarn('[ChatStore] addRoom: Não foi possível adicionar sala', summarizeRoom(room))
      }
      return created
    },
    upsertRoom(room: any) {
      const normalized = this.normalizeRoomPayload(room)
      if (!normalized) {
        return false
      }

      const roomKey = String(normalized.roomId)
      if (!this.messagesByRoomId[roomKey]) {
        this.messagesByRoomId[roomKey] = []
      }

      const existingIndex = this.rooms.findIndex((existingRoom) => String(existingRoom?.roomId) === roomKey)

      const unreadFromPayload = typeof room?.unreadMessages === 'number' ? room.unreadMessages : normalized.unreadCount

      if (existingIndex === -1) {
        const roomToAdd: Room = {
          ...normalized,
          unreadCount: unreadFromPayload,
        }
        this.rooms.push(roomToAdd)
        devLog('[ChatStore] upsertRoom: Sala criada', { roomId: roomToAdd.roomId })
      } else {
        const current = this.rooms[existingIndex]
        const merged: Room = {
          ...current,
          ...normalized,
          unreadCount: unreadFromPayload ?? current.unreadCount,
        }
        this.rooms.splice(existingIndex, 1, merged)
        devLog('[ChatStore] upsertRoom: Sala atualizada', { roomId: merged.roomId })
      }

      this.lastCommunicationByRoom[normalized.roomId] = normalized.last_message_at

      if (room?.last_communication?.direction === 'incoming' && room?.last_communication?.created_at) {
        this.lastIncomingByRoom[normalized.roomId] = room.last_communication.created_at
      }

      return true
    },
    removeRoom(roomId: string | number) {
      const roomKey = String(roomId)
      const roomIndex = this.rooms.findIndex((room) => String(room.roomId) === roomKey)
      if (roomIndex !== -1) {
        this.rooms.splice(roomIndex, 1)
      }
      delete this.messagesByRoomId[roomKey]
      delete this.lastCommunicationByRoom[roomKey]
      delete this.lastIncomingByRoom[roomKey]
    },
    clearAllRooms() {
      this.rooms = []
      this.messagesByRoomId = {}
      this.lastCommunicationByRoom = {}
      this.lastIncomingByRoom = {}
    },
    updateUnreadCountForRoom(roomId: string | number, increment = 1) {
      const roomKey = String(roomId)
      const roomIndex = this.rooms.findIndex((room) => String(room.roomId) === roomKey)
      if (roomIndex === -1) {
        devDebug('[ChatStore] updateUnreadCountForRoom: Sala não encontrada', { roomId })
        return
      }

      const currentRoom = this.rooms[roomIndex]
      const currentUnread = Number(currentRoom.unreadCount) || 0
      const updatedRoom = { ...currentRoom }

      if (increment > 0) {
        const nextUnread = currentUnread + increment
        updatedRoom.unreadCount = nextUnread
        updatedRoom.last_message_at = new Date().toISOString()
      } else {
        updatedRoom.unreadCount = 0
      }

      this.rooms.splice(roomIndex, 1, updatedRoom)
      devLog('[Pinia] updateUnreadCountForRoom', { roomId: roomKey, unreadCount: updatedRoom.unreadCount })
    },
    updateLastCommunication(roomId: string | number, timestamp: string | number) {
      this.lastCommunicationByRoom[roomId] = timestamp
      // Também refletir na sala para que sortedRooms reaja
      const roomIndex = this.rooms.findIndex((room) => room.roomId === roomId)
      if (roomIndex !== -1) {
        try {
          const iso =
            typeof timestamp === 'number' ? new Date(timestamp).toISOString() : new Date(timestamp).toISOString()
          if (!isNaN(new Date(iso).getTime())) {
            this.rooms[roomIndex].last_message_at = iso
          }
        } catch (e) {
          // fallback: mantém valor atual
        }
      }
    },
    updateLastIncoming(roomId: string | number, timestamp: string | number) {
      this.lastIncomingByRoom[roomId] = timestamp
    },
    updateRoom(roomId: string | number, updatedData: any, temperatures: any) {
      devLog('[Pinia] updateRoom: Atualizando sala', {
        roomId,
        updatedKeys: Object.keys(updatedData || {}),
      })
      const roomKey = String(roomId)
      const roomIndex = this.rooms.findIndex((room) => String(room.roomId) === roomKey)

      if (roomIndex === -1) {
        devWarn('[Pinia] updateRoom: Sala não encontrada', { roomId })
        return
      }

      const currentRoom = this.rooms[roomIndex]
      const resolvedTemperature =
        temperatures.find((t: any) => t.name === updatedData?.temperature) || currentRoom.temperature

      const normalizedTypingUsers = Array.isArray(updatedData?.typingUsers)
        ? updatedData.typingUsers.map((id: any) => String(id))
        : currentRoom.typingUsers || []

      const unreadFromPayload =
        typeof updatedData?.unreadMessages === 'number'
          ? updatedData.unreadMessages
          : typeof updatedData?.unreadCount === 'number'
            ? updatedData.unreadCount
            : currentRoom.unreadCount

      const lastCommunication = updatedData?.last_communication || currentRoom.last_communication || null
      const lastMessageAt =
        updatedData?.last_message_at ||
        (lastCommunication && lastCommunication.created_at) ||
        currentRoom.last_message_at

      const mergedRoom: Room = {
        ...currentRoom,
        ...updatedData,
        current_stage: updatedData?.current_stage || currentRoom.current_stage,
        assigned_to_details: updatedData?.assigned_to_details || currentRoom.assigned_to_details,
        temperature: resolvedTemperature,
        unreadCount: unreadFromPayload,
        last_message_at: lastMessageAt,
        typingUsers: normalizedTypingUsers,
      }

      this.rooms.splice(roomIndex, 1, mergedRoom)
      devLog('[Pinia] updateRoom: Sala atualizada', {
        roomId,
        unreadCount: mergedRoom.unreadCount,
        temperature: mergedRoom.temperature?.name ?? mergedRoom.temperature,
      })

      if (lastMessageAt) {
        this.lastCommunicationByRoom[roomKey] = lastMessageAt
      }

      if (lastCommunication?.direction === 'incoming' && lastCommunication?.created_at) {
        this.lastIncomingByRoom[roomKey] = lastCommunication.created_at
      }
    },
    setTypingStatus(roomId: string | number, userId: string | number, isTyping: boolean) {
      const safeRoomId = String(roomId)
      const safeUserId = String(userId)

      if (!safeRoomId || safeRoomId === 'undefined' || safeRoomId === 'null') {
        devWarn('[ChatStore] setTypingStatus: roomId inválido', { roomId })
        return
      }

      if (!safeUserId || safeUserId === 'undefined' || safeUserId === 'null') {
        devWarn('[ChatStore] setTypingStatus: userId inválido', { userId })
        return
      }

      const roomIndex = this.rooms.findIndex((room) => String(room.roomId) === safeRoomId)
      if (roomIndex === -1) {
        devWarn('[ChatStore] setTypingStatus: Sala não encontrada', { roomId: safeRoomId })
        return
      }

      const currentRoom = this.rooms[roomIndex]
      const currentTyping = Array.isArray(currentRoom.typingUsers) ? currentRoom.typingUsers : []

      let updatedTyping: string[]

      if (isTyping) {
        if (currentTyping.includes(safeUserId)) {
          return
        }
        updatedTyping = [...currentTyping, safeUserId]
      } else {
        if (!currentTyping.includes(safeUserId)) {
          return
        }
        updatedTyping = currentTyping.filter((id) => id !== safeUserId)
      }

      this.rooms[roomIndex] = {
        ...currentRoom,
        typingUsers: updatedTyping,
      }
    },
    clearTypingUsers(roomId: string | number) {
      const safeRoomId = String(roomId)
      if (!safeRoomId || safeRoomId === 'undefined' || safeRoomId === 'null') {
        return
      }

      const roomIndex = this.rooms.findIndex((room) => String(room.roomId) === safeRoomId)
      if (roomIndex === -1) {
        return
      }

      const currentRoom = this.rooms[roomIndex]
      if (!currentRoom.typingUsers || currentRoom.typingUsers.length === 0) {
        return
      }

      this.rooms[roomIndex] = {
        ...currentRoom,
        typingUsers: [],
      }
    },
    updateMessageFile(messageId: string | number, fileIndex: number, fileUpdate: any) {
      // ✅ FASE 1: Função para atualizar arquivo específico em uma mensagem
      devDebug('[ChatStore] updateMessageFile', { messageId, fileIndex, updateKeys: Object.keys(fileUpdate || {}) })

      // Buscar mensagem em todas as salas
      for (const [roomId, messages] of Object.entries(this.messagesByRoomId)) {
        const messageIndex = messages.findIndex(
          (m) => m._id === messageId || m.id === messageId || m.temporary_id === messageId,
        )

        if (messageIndex !== -1) {
          const message = messages[messageIndex]

          // Garantir que files existe e é um array
          if (!message.files || !Array.isArray(message.files)) {
            devWarn('[ChatStore] updateMessageFile: Mensagem sem files array')
            return false
          }

          // Verificar se o índice é válido
          if (fileIndex < 0 || fileIndex >= message.files.length) {
            devWarn('[ChatStore] updateMessageFile: Índice de arquivo inválido', {
              fileIndex,
              filesLength: message.files.length,
            })
            return false
          }

          // ✅ CORREÇÃO: Atualizar arquivo específico preservando dados importantes
          const updatedFiles = [...message.files]
          const currentFile = updatedFiles[fileIndex]
          const updatedFile = {
            ...currentFile,
            ...fileUpdate,
          }

          // ✅ PRESERVAÇÃO INTELIGENTE: Se o arquivo atual tem preview e o update não
          // preservar o preview atual (importante para transição suave)
          if (currentFile.preview && !fileUpdate.preview && !fileUpdate.definitive) {
            updatedFile.preview = currentFile.preview
          }

          // ✅ PRESERVAÇÃO INTELIGENTE: Se o arquivo atual tem URL e o update não
          // preservar a URL atual (importante para não quebrar preview)
          if (currentFile.url && !fileUpdate.url && !fileUpdate.definitive) {
            updatedFile.url = currentFile.url
          }

          // ✅ LIMPEZA: Se é um update definitivo, limpar URLs temporárias antigas
          if (fileUpdate.definitive && currentFile.url && currentFile.url.startsWith('blob:')) {
            // Agendar limpeza da URL antiga
            setTimeout(() => {
              try {
                URL.revokeObjectURL(currentFile.url)
              } catch (e) {
                // Ignorar erros de revogação
              }
            }, 1000) // 1 segundo para transição suave
          }

          updatedFiles[fileIndex] = updatedFile

          // Atualizar mensagem com novo array de arquivos
          const updatedMessages = [...messages]
          updatedMessages[messageIndex] = {
            ...message,
            files: updatedFiles,
          }

          // Garantir imutabilidade do estado
          this.messagesByRoomId[roomId] = updatedMessages

          devDebug('[ChatStore] updateMessageFile: Arquivo atualizado com sucesso', {
            roomId,
            messageId,
            fileIndex,
            updateKeys: Object.keys(fileUpdate || {}),
          })

          return true
        }
      }

      devWarn('[ChatStore] updateMessageFile: Mensagem não encontrada', { messageId })
      return false
    },
  },
  getters: {
    sortedRooms(state): Room[] {
      if (!state.rooms || state.rooms.length === 0) return []

      const getRoomTime = (r: Room) => {
        const raw = (r && (r as any).last_message_at) || state.lastCommunicationByRoom[r.roomId]
        if (!raw) return 0
        if (typeof raw === 'number') return raw
        const d = new Date(raw as any)
        if (!isNaN(d.getTime())) return d.getTime()
        // Fallback para BR
        const br = DateManager.parseBRDate(raw as any) || DateManager.parseBRDate(raw as any, 'dd/MM/yyyy HH:mm')
        return br ? br.getTime() : 0
      }

      const sortByDateDesc = (a: Room, b: Room) => getRoomTime(b) - getRoomTime(a)

      const unreadRooms = state.rooms.filter((r) => r.unreadCount > 0).sort(sortByDateDesc)
      const readRooms = state.rooms.filter((r) => r.unreadCount === 0).sort(sortByDateDesc)

      return [...unreadRooms, ...readRooms]
    },
    messagesForRoom:
      (state) =>
      (roomId: string | number): Message[] => {
        return state.messagesByRoomId[roomId] || []
      },
    unreadCount(state): number {
      return state.rooms.reduce((acc, room) => acc + (room.unreadCount || 0), 0)
    },
  },
})
